import { createContext, useContext, useEffect, useState } from "react";
import { api, type HelperLimits } from "./api";

// Feature switches the teacher controls (decision 56; apps/api/src/settings.ts). Any screen can ask
// useFeature("surpriseMe"); unknown or not-yet-loaded switches read as off.

export const FeaturesContext = createContext<Record<string, boolean>>({});
export const useFeature = (key: string) => !!useContext(FeaturesContext)[key];

/** Shown to co-teachers on the settings cards: they can see the settings, only an owner changes them. */
export const OWNER_ONLY_NOTE = "Only the owner can change these.";

/** The teacher's switches on Home. Ones that need school leadership's OK are marked. Co-teachers see them read-only. */
export function FeaturesEntry({ onChange }: { onChange: (features: Record<string, boolean>) => void }) {
  const [list, setList] = useState<{ key: string; label: string; leadership: boolean; on: boolean }[] | null>(null);
  const [canChange, setCanChange] = useState(false);
  useEffect(() => {
    void api.settings().then(
      (r) => {
        setList(r.features);
        setCanChange(r.canChange);
      },
      () => setList(null),
    );
  }, []);
  if (!list) return null;
  return (
    <section aria-label="Features" className="subs-entry features-entry">
      <div>
        <h2>Features</h2>
        <p className="muted small">Turn parts of BuildLab on or off for everyone. Ones marked “leadership” stay off until the school says yes.</p>
        {!canChange && <p className="muted small owner-only-note">{OWNER_ONLY_NOTE}</p>}
      </div>
      <ul className="feature-list">
        {list.map((f) => (
          <li key={f.key}>
            <label className="check">
              <input
                type="checkbox"
                checked={f.on}
                disabled={!canChange}
                onChange={async (e) => {
                  const on = e.target.checked;
                  await api.setFeature(f.key, on);
                  const next = list.map((x) => (x.key === f.key ? { ...x, on } : x));
                  setList(next);
                  onChange(Object.fromEntries(next.map((x) => [x.key, x.on])));
                }}
              />
              {f.label}
              {f.leadership && <span className="badge-leadership">leadership</span>}
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The teacher's helper limits on Home (FR-28; apps/api/src/guardrails.ts). Teachers are never limited. */
export function HelperLimitsEntry() {
  const [saved, setSaved] = useState<HelperLimits | null>(null);
  const [draft, setDraft] = useState({ dailyTurns: "", classMonthlyUsd: "" });
  const [note, setNote] = useState<string | null>(null);
  const [canChange, setCanChange] = useState(false);
  const show = (l: HelperLimits) => {
    setSaved(l);
    setDraft({ dailyTurns: String(l.dailyTurns), classMonthlyUsd: String(l.classMonthlyUsd) });
  };
  useEffect(() => {
    void api.limits().then(
      (r) => {
        show(r.limits);
        setCanChange(r.canChange);
      },
      () => setSaved(null),
    );
  }, []);
  if (!saved) return null;
  const changed = draft.dailyTurns !== String(saved.dailyTurns) || draft.classMonthlyUsd !== String(saved.classMonthlyUsd);
  return (
    <section aria-label="Helper limits" className="subs-entry limits-entry">
      <div>
        <h2>Helper limits</h2>
        <p className="muted small">For students (and guests) only. Give one student 10 more for today from their tile in the live view.</p>
        {!canChange && <p className="muted small owner-only-note">{OWNER_ONLY_NOTE}</p>}
      </div>
      <form
        className="limits-form"
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            const r = await api.setLimits({ dailyTurns: Number(draft.dailyTurns), classMonthlyUsd: Number(draft.classMonthlyUsd) });
            show(r.limits);
            setNote("Saved.");
          } catch (err) {
            setNote((err as Error).message);
          }
        }}
      >
        <label>
          Helper messages per student per day
          <input id="limit-daily" disabled={!canChange} type="number" min={1} max={500} value={draft.dailyTurns} onChange={(e) => (setDraft({ ...draft, dailyTurns: e.target.value }), setNote(null))} />
        </label>
        <label>
          Budget per class per month ($)
          <input id="limit-budget" disabled={!canChange} type="number" min={1} max={1000} step="0.01" value={draft.classMonthlyUsd} onChange={(e) => (setDraft({ ...draft, classMonthlyUsd: e.target.value }), setNote(null))} />
        </label>
        <button type="submit" className="outline small" disabled={!changed || !canChange}>
          Save limits
        </button>
        {note && <span className="muted small">{note}</span>}
      </form>
    </section>
  );
}
